import { useState, useEffect } from 'react'
import { SelectedDimensions } from './components/financial/gl-coding/types'

interface GLCodingHistoryItem {
  dimensions: SelectedDimensions
  glCode: string
  timestamp: number
  useCount?: number
}

interface FavoriteItem {
  id: string
  name: string
  dimensions: SelectedDimensions
  glCode: string
}

const RECENT_KEY = 'gl_coding_recent'
const FAVORITES_KEY = 'gl_coding_favorites'
const USAGE_STATS_KEY = 'gl_coding_usage_stats'
const MAX_RECENT = 5

export function useGLCodingHistory() {
  const [recent, setRecent] = useState<GLCodingHistoryItem[]>([])
  const [favorites, setFavorites] = useState<FavoriteItem[]>([])
  const [usageStats, setUsageStats] = useState<Record<string, number>>({})

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const recentStr = localStorage.getItem(RECENT_KEY)
      const favoritesStr = localStorage.getItem(FAVORITES_KEY)
      const usageStatsStr = localStorage.getItem(USAGE_STATS_KEY)

      if (recentStr) {
        setRecent(JSON.parse(recentStr))
      }
      if (favoritesStr) {
        setFavorites(JSON.parse(favoritesStr))
      }
      if (usageStatsStr) {
        setUsageStats(JSON.parse(usageStatsStr))
      }
    } catch (error) {
      console.error('Failed to load GL coding history:', error)
    }
  }, [])

  // Add to recent selections and track usage
  const addToRecent = (dimensions: SelectedDimensions, glCode: string) => {
    const newItem: GLCodingHistoryItem = {
      dimensions,
      glCode,
      timestamp: Date.now(),
    }

    // Update recent list
    const updated = [
      newItem,
      ...recent.filter((item) => item.glCode !== glCode),
    ].slice(0, MAX_RECENT)

    setRecent(updated)
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated))

    // Update usage stats
    const newUsageStats = {
      ...usageStats,
      [glCode]: (usageStats[glCode] || 0) + 1,
    }
    setUsageStats(newUsageStats)
    localStorage.setItem(USAGE_STATS_KEY, JSON.stringify(newUsageStats))
  }

  // Get top used GL codes
  const getTopUsed = (limit: number = 10): GLCodingHistoryItem[] => {
    // Sort by usage count
    const sortedCodes = Object.entries(usageStats)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, limit)

    // Find the full items from recent history
    return sortedCodes
      .map(([glCode, useCount]) => {
        const item = recent.find((r) => r.glCode === glCode)
        return item ? { ...item, useCount } : null
      })
      .filter((item): item is GLCodingHistoryItem => item !== null)
  }

  // Add to favorites
  const addToFavorites = (
    name: string,
    dimensions: SelectedDimensions,
    glCode: string,
  ) => {
    const newFavorite: FavoriteItem = {
      id: Date.now().toString(),
      name,
      dimensions,
      glCode,
    }

    const updated = [...favorites, newFavorite]
    setFavorites(updated)
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated))
  }

  // Remove from favorites
  const removeFromFavorites = (id: string) => {
    const updated = favorites.filter((fav) => fav.id !== id)
    setFavorites(updated)
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated))
  }

  // Check if a GL code is favorited
  const isFavorite = (glCode: string): boolean => {
    return favorites.some((fav) => fav.glCode === glCode)
  }

  return {
    recent,
    favorites,
    topUsed: getTopUsed(),
    addToRecent,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    getUsageCount: (glCode: string) => usageStats[glCode] || 0,
  }
}
